import type { ClientProfile, ProfileTabId } from '../../data/types'
import { useCallback, useEffect, useState } from 'react'
import { displayName } from '../../data/mockClients'
import { getClientExtras } from '../../data/clientExtras'
import type { RecordKey } from '../../data/clientRecords'
import { ApiError } from '../../api/auth'
import {
  createClientRecord,
  deleteClientRecord,
  listClientRecords,
  updateClientRecord,
  type ClientRecord,
} from '../../api/clientRecords'
import { Icon, type IconName } from '../../components/Icon'

/* ---------------- fact resolution ---------------- */

type FactKey =
  | 'fullName'
  | 'id'
  | 'dob'
  | 'age'
  | 'gender'
  | 'marital'
  | 'language'
  | 'physician'
  | 'physicianPhone'
  | 'address'
  | 'status'
  | 'cell'
  | 'home'
  | 'medicaid'
  | 'medicare'
  | 'admissionDate'
  | 'program'
  | 'careManager'

function ageFromDob(dob?: string): string {
  if (!dob) return ''
  const m = dob.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (!m) return ''
  const birth = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  if (Number.isNaN(birth.getTime())) return ''
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const md = now.getMonth() - birth.getMonth()
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age--
  return `${age} yrs`
}

function resolveFact(key: FactKey, client: ClientProfile): string {
  const x = getClientExtras(client.id)
  const p = (name: string): string | undefined => {
    const value = client.profile?.[name]
    return typeof value === 'string' && value.trim() ? value : undefined
  }
  const cityLine = [p('cli_city') || x.city, p('cli_state') || x.state].filter(Boolean).join(', ')
  const addressBlock = client.address || [p('cli_address') || x.address, [cityLine, p('cli_zip') || x.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ')
  const map: Record<FactKey, string | undefined> = {
    fullName: displayName(client),
    id: client.id,
    dob: client.dob,
    age: ageFromDob(client.dob),
    gender: client.gender || p('cli_sex') || x.gender,
    marital: p('cli_material_status') || x.maritalStatus,
    language: p('cli_primary_lang') || x.primaryLanguage,
    physician: x.physicianName,
    physicianPhone: x.physicianPhone,
    address: addressBlock,
    status: client.status,
    cell: client.phoneCell,
    home: client.phoneHome,
    medicaid: client.medicaidId || p('cli_medicaid') || x.medicaidId,
    medicare: client.medicareId || p('cli_medicare') || x.medicareId,
    admissionDate: client.admissionDate || p('cli_adm_date') || x.admissionDate,
    program: client.program || p('cli_program_name'),
    careManager: client.careManager || p('cli_mltss_manager_name'),
  }
  return map[key] || ''
}

/* ---------------- block model ---------------- */

type Block =
  | { kind: 'facts'; title: string; icon: IconName; fields: { label: string; key: FactKey }[] }
  | {
      kind: 'table'
      title: string
      icon: IconName
      columns: string[]
      record?: RecordKey
      addLabel?: string
      empty?: string
      filters?: string[]
    }
  | { kind: 'outline'; title: string; icon: IconName; items: string[]; note?: string }

type TabConfig = {
  blocks: Block[]
}

/* ---------------- tab configs ---------------- */

export const SECTIONS: Partial<Record<ProfileTabId, TabConfig>> = {
  admission: {
    blocks: [
      { kind: 'facts', title: 'Client', icon: 'user', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Age', key: 'age' },
        { label: 'Gender', key: 'gender' },
        { label: 'Status', key: 'status' },
      ] },
      { kind: 'outline', title: 'Admission & consent forms', icon: 'reports', note: 'Completed forms are stored per client.', items: [
        'Admission Policy', 'Advance Directives Information', 'Covid Screening',
        'Food Program Application', 'Home Assessment', 'Informed Consent',
        'Initial Intake Assessment', 'Intake PA Form', 'Photo Consent',
      ] },
    ],
  },
  nursing: {
    blocks: [
      { kind: 'facts', title: 'Client', icon: 'user', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Primary physician', key: 'physician' },
      ] },
      { kind: 'table', title: 'Client diagnosis', icon: 'clinical', record: 'diagnoses',
        addLabel: 'Add diagnosis', empty: 'No diagnoses recorded.',
        columns: ['Date', 'Diagnosis', 'Code', 'Physician', 'Status'] },
      { kind: 'outline', title: 'Nursing assessments', icon: 'reports', items: [
        'Client Allergies', 'Code Status', 'Interdisciplinary Assessment',
        'Medication Self-Administration', 'Morse Fall Scale', 'Nursing Assessment',
        'Re-admission Assessment', 'Wandering Risk Scale',
      ] },
    ],
  },
  social: {
    blocks: [
      { kind: 'facts', title: 'Social assessment', icon: 'user', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Gender', key: 'gender' },
        { label: 'Primary language', key: 'language' },
        { label: 'Status', key: 'status' },
      ] },
      { kind: 'table', title: 'Family members', icon: 'users', record: 'family',
        addLabel: 'Add family member', empty: 'No family members recorded.',
        columns: ['Name', 'Residing with client', 'Age', 'Relationship', 'Rating'] },
      { kind: 'outline', title: 'Assessment covers', icon: 'reports', items: [
        'Family & community support', 'Transportation', 'Substance & smoking history',
        'Legal & guardianship', 'Behaviors & advance directives', 'Goals',
      ] },
    ],
  },
  activity: {
    blocks: [
      { kind: 'facts', title: 'Activity assessment', icon: 'activities', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Gender', key: 'gender' },
        { label: 'Marital status', key: 'marital' },
        { label: 'Primary language', key: 'language' },
        { label: 'Physician', key: 'physician' },
      ] },
      { kind: 'outline', title: 'Assessment covers', icon: 'reports', items: [
        'Functional status (vision, hearing, mobility)', 'Orientation & memory',
        'Behavior traits', 'Activity interests', 'Hobbies & goals',
      ] },
    ],
  },
  dietary: {
    blocks: [
      { kind: 'facts', title: 'Dietary assessment', icon: 'food', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Gender', key: 'gender' },
      ] },
      { kind: 'outline', title: 'Assessment covers', icon: 'reports', items: [
        'Diagnosis & allergies', 'Diet plan & recommended diet', 'Weight & BMI ranges',
        'Appetite & feeding level', 'Food preferences / dislikes', 'Dentition',
      ] },
    ],
  },
  hp: {
    blocks: [
      { kind: 'facts', title: 'History & physical', icon: 'clinical', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Date of birth', key: 'dob' },
        { label: 'Primary physician', key: 'physician' },
      ] },
      { kind: 'table', title: 'Medications', icon: 'clinical', record: 'medications',
        addLabel: 'Add medication', empty: 'No medications recorded.',
        columns: ['Medication', 'Dosage', 'Form', 'Frequency', 'Route'] },
      { kind: 'outline', title: 'Physical exam', icon: 'reports', items: [
        'Skin', 'HEENT', 'Neck', 'Chest & lungs', 'Heart', 'Abdomen',
        'Genitourinary', 'Musculoskeletal', 'Neurological',
      ] },
    ],
  },
  immunization: {
    blocks: [
      { kind: 'table', title: 'Immunizations', icon: 'shield', record: 'immunizations',
        addLabel: 'Add immunization', empty: 'No immunizations on record.',
        columns: ['Date administered', 'Immunization', 'Step', 'Physician', 'Next due', 'Status'] },
    ],
  },
  notes: {
    blocks: [
      { kind: 'table', title: 'Progress notes', icon: 'reports', record: 'notes',
        addLabel: 'Add note', empty: 'No progress notes yet.',
        columns: ['Title', 'Note date', 'Taken by', 'Follow-up date'] },
    ],
  },
  'care-plans': {
    blocks: [
      { kind: 'table', title: 'Care plans', icon: 'heart', record: 'carePlans',
        addLabel: 'Add care plan', empty: 'No care plans on record.',
        columns: ['Assessment date', 'Template', 'Completed date', 'Follow-up date', 'Next due date'] },
    ],
  },
  assessments: {
    blocks: [
      { kind: 'table', title: 'Assessments', icon: 'reports', record: 'assessments',
        addLabel: 'Add assessment', empty: 'No assessments on record.',
        columns: ['Assessment', 'Assessment date', 'Completed date', 'Follow-up date', 'Next due date'] },
    ],
  },
  vitals: {
    blocks: [
      { kind: 'table', title: 'Vitals', icon: 'chart', record: 'vitals',
        addLabel: 'Add vital', empty: 'No vitals recorded.',
        columns: ['Performed', 'Scheduled', 'Vital sign', 'Value', 'Time', 'Range', 'Recorded by'] },
    ],
  },
  mars: {
    blocks: [
      { kind: 'table', title: 'Medication administration (MARS)', icon: 'clinical', record: 'mars',
        addLabel: 'MAR profile', empty: 'No medication administration records.',
        columns: ['Scheduled date', 'Medication', 'Dosage', 'Given?', 'Time given', 'Reason / comment', 'Nurse'] },
    ],
  },
  adls: {
    blocks: [
      { kind: 'table', title: 'ADL tracking log', icon: 'tasks', record: 'adls',
        addLabel: 'Add ADL', empty: 'No ADL entries recorded.',
        columns: ['Date', 'Bathing & dressing', 'Toilet use', 'Transfer', 'Locomotion', 'Eating', 'Bed mobility'] },
    ],
  },
  hospitalization: {
    blocks: [
      { kind: 'table', title: 'Hospitalization', icon: 'clinical', record: 'hospitalizations',
        addLabel: 'Add hospitalization', empty: 'No hospitalizations on record.',
        columns: ['Transfer from', 'Admission date', 'Discharge date', 'Transfer to', 'Emergency room', 'Order by'] },
    ],
  },
  documents: {
    blocks: [
      { kind: 'table', title: 'Documents', icon: 'reports', record: 'documents',
        addLabel: 'Upload document', empty: 'No documents uploaded.',
        columns: ['Title', 'Type', 'Document date', 'Expiration', 'Uploaded by', 'File'] },
    ],
  },
  incident: {
    blocks: [
      { kind: 'table', title: 'Incident reports', icon: 'shield', record: 'incidents',
        addLabel: 'Add incident', empty: 'No incidents reported.',
        columns: ['Date / time', 'Location', 'Type', 'Completed by'] },
    ],
  },
  orders: {
    blocks: [
      { kind: 'table', title: 'Orders', icon: 'reports', record: 'orders',
        addLabel: 'Add order', empty: 'No active orders.', filters: ['Status: Active'],
        columns: ['Title', 'Order date', 'Discipline', 'Next due date', 'Order by', 'Communication'] },
    ],
  },
  log: {
    blocks: [
      { kind: 'table', title: 'Attendance log', icon: 'clock', record: 'log',
        addLabel: 'Add entry', empty: 'No attendance records in range.',
        filters: ['Type: Facility', 'Status: Present'],
        columns: ['Date', 'Type', 'Status', 'Shift', 'Notes'] },
    ],
  },
  sdoh: {
    blocks: [
      { kind: 'table', title: 'Social determinants of health', icon: 'crm', record: 'sdoh',
        addLabel: 'Add SDOH assessment', empty: 'No SDOH assessments on record.',
        columns: ['Date', 'Assessment', 'Score', 'Completed by'] },
    ],
  },
  'adv-adl': {
    blocks: [
      { kind: 'table', title: 'Advanced ADL', icon: 'tasks', record: 'advAdl',
        addLabel: 'Add entry', empty: 'No advanced ADL entries.',
        columns: ['Date', 'Activity', 'Result', 'Recorded by'] },
    ],
  },
  'upload-pic': {
    blocks: [
      { kind: 'table', title: 'Pictures', icon: 'user', record: 'pictures',
        addLabel: 'Upload picture', empty: 'No pictures uploaded.',
        columns: ['Date', 'Title', 'Uploaded by'] },
    ],
  },
  schedule: {
    blocks: [
      { kind: 'facts', title: 'Client', icon: 'user', fields: [
        { label: 'Full name', key: 'fullName' },
        { label: 'Address', key: 'address' },
        { label: 'Home phone', key: 'home' },
      ] },
      { kind: 'table', title: 'Shift schedule', icon: 'calendar', record: 'schedule',
        addLabel: 'Assign shift', empty: 'No shifts assigned for this period.',
        columns: ['Date', 'Day', 'Shift', 'Schedule'] },
    ],
  },
}

/* ---------------- renderers ---------------- */

function FactsBlock({ block, client }: { block: Extract<Block, { kind: 'facts' }>; client: ClientProfile }) {
  return (
    <div className="card">
      <div className="card__head">
        <span className="fs-ic"><Icon name={block.icon} size={18} /></span>
        <h3>{block.title}</h3>
      </div>
      <div className="card__body">
        <div className="facts">
          {block.fields.map((f) => {
            const v = resolveFact(f.key, client)
            return (
              <div key={f.label} className="fact">
                <span className="fact__k">{f.label}</span>
                <span className={`fact__v${v ? '' : ' is-empty'}`}>{v || 'Not on file'}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function TableBlock({ block, client }: { block: Extract<Block, { kind: 'table' }>; client: ClientProfile }) {
  const section = block.record
  const [rows, setRows] = useState<ClientRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<ClientRecord | null | undefined>(undefined)
  const [values, setValues] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!section) return
    setLoading(true)
    setError('')
    try {
      setRows(await listClientRecords(client.id, section))
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to load records.')
    } finally {
      setLoading(false)
    }
  }, [client.id, section])

  useEffect(() => {
    void load()
  }, [load])

  const openAdd = () => {
    setValues(block.columns.map(() => ''))
    setEditing(null)
  }

  const openEdit = (record: ClientRecord) => {
    setValues(block.columns.map((_, index) => record.values[index] || ''))
    setEditing(record)
  }

  const save = async () => {
    if (!section || values.every((value) => !value.trim())) {
      setError('Enter at least one value.')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editing) await updateClientRecord(client.id, editing, values)
      else await createClientRecord(client.id, section, values)
      setEditing(undefined)
      await load()
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save this record.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (record: ClientRecord) => {
    if (!window.confirm('Remove this record?')) return
    try {
      await deleteClientRecord(client.id, record.id)
      await load()
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to remove this record.')
    }
  }

  return (
    <div className="card">
      <div className="card__head">
        <span className="fs-ic"><Icon name={block.icon} size={18} /></span>
        <h3>{block.title}</h3>
        <div style={{ flex: '1' }} />
        {block.filters?.map((f) => (
          <span key={f} className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}>{f}</span>
        ))}
        {block.addLabel ? (
          <button type="button" className="btn btn--primary btn--sm" onClick={openAdd}>
            <Icon name="plus" /> {block.addLabel}
          </button>
        ) : null}
      </div>
      {error ? <p className="form-error" style={{ margin: '.75rem 1rem' }}>{error}</p> : null}
      {loading ? (
        <div className="empty"><p>Loading records…</p></div>
      ) : rows.length ? (
        <div className="table-wrap" style={{ border: '0', boxShadow: 'none', borderRadius: '0' }}>
          <table className="data-table">
            <thead>
              <tr>{block.columns.map((c) => <th key={c}>{c}</th>)}<th>Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={{ cursor: 'default' }}>
                  {block.columns.map((c, j) => (
                    <td key={c} className={j === 0 ? '' : 'muted'}>{row.values[j] || '—'}</td>
                  ))}
                  <td><div className="cell-actions">
                    <button type="button" className="act-btn" title="Edit record" onClick={() => openEdit(row)}><Icon name="edit" /></button>
                    <button type="button" className="act-btn" title="Remove record" onClick={() => void remove(row)}><Icon name="reports" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty">
          <Icon name="reports" />
          <p>{block.empty || 'No records.'}</p>
        </div>
      )}
      {editing !== undefined ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setEditing(undefined)}>
          <div className="modal-panel" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${block.title}` : block.addLabel} onClick={(event) => event.stopPropagation()}>
            <div className="modal-panel__head">
              <h2>{editing ? `Edit ${block.title}` : block.addLabel}</h2>
              <button type="button" className="icon-btn" aria-label="Close" onClick={() => setEditing(undefined)}>✕</button>
            </div>
            <div className="modal-panel__body"><div className="form-grid">
              {block.columns.map((column, index) => (
                <label className="field" key={column}><span>{column}</span><input value={values[index] || ''} onChange={(event) => setValues((current) => current.map((value, i) => i === index ? event.target.value : value))} /></label>
              ))}
            </div></div>
            <div className="modal-panel__foot">
              <div style={{ flex: 1 }} />
              <button type="button" className="btn btn--ghost" onClick={() => setEditing(undefined)}>Cancel</button>
              <button type="button" className="btn btn--primary" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function OutlineBlock({ block }: { block: Extract<Block, { kind: 'outline' }> }) {
  return (
    <div className="card">
      <div className="card__head">
        <span className="fs-ic"><Icon name={block.icon} size={18} /></span>
        <h3>{block.title}</h3>
      </div>
      <div className="card__body">
        <ul className="outline-list">
          {block.items.map((it) => (
            <li key={it}><Icon name="check" size={16} /> {it}</li>
          ))}
        </ul>
        {block.note ? <p className="muted" style={{ margin: '.9rem 0 0', fontSize: '.82rem' }}>{block.note}</p> : null}
      </div>
    </div>
  )
}

export function ClinicalTab({ tabId, client }: { tabId: ProfileTabId; client: ClientProfile }) {
  const config = SECTIONS[tabId]
  if (!config) return null
  return (
    <div className="tab-stack" style={{ marginTop: '1rem' }}>
      {config.blocks.map((block, i) => {
        if (block.kind === 'facts') return <FactsBlock key={i} block={block} client={client} />
        if (block.kind === 'table') return <TableBlock key={i} block={block} client={client} />
        return <OutlineBlock key={i} block={block} />
      })}
    </div>
  )
}

export function hasNativeSection(tabId: string): tabId is ProfileTabId {
  return tabId in SECTIONS
}
